import { useIsAuthenticated } from '@azure/msal-react';
import { useQuery } from '@apollo/client';
import { useEffect } from 'react';
import {
    DialogProvider,
    GuardianProvider,
    PopupProvider,
    InfoBox,
    LoadingIndicator,
} from '@ics-portal/guardian';
import type { GetMeQuery } from '@gql/graphql';
import { ME } from '@api/queries/users';
import { getRuntimeAuthToken } from '@auth/runtime-token';
import { GlobalUiProvider } from '@contexts/GlobalUiContext';
import { InfoCardProvider } from '@components/InfoCard';
import { GlobalDataProvider } from '@contexts/GlobalDataContext';
import WelcomePage from './pages/WelcomePage';
import App from './App';
import useCorrelationId from './hooks/useCorrelationId';

export default function Root() {
    /**
     * Generate and store a correlation id for the current session
     * used to correlate the frontend logs with the backend logs
     * Is sent both to the backend API and Sentry
     */
    useCorrelationId();

    /**
     * Handle a preload error, for example, if a non-existing module is lazy-loaded
     * which can happen when the user is on an old version of the app and the module has been removed
     * @see https://vitejs.dev/guide/build#load-error-handling
     */
    useEffect(() => {
        function handlePreloadError() {
            window.location.reload();
        }

        window.addEventListener('vite:preloadError', handlePreloadError);

        return () => {
            window.removeEventListener('vite:preloadError', handlePreloadError);
        };
    }, []);

    const runtimeToken = getRuntimeAuthToken();
    const isAuthenticated =
        useIsAuthenticated() ||
        (import.meta.env.DEV &&
            import.meta.env.VITE_LOCAL_AUTH_BYPASS === 'true') ||
        Boolean(runtimeToken);
    const { data, error, loading } = useQuery<GetMeQuery>(ME, {
        fetchPolicy: 'network-only',
        skip: !runtimeToken,
    });

    if (runtimeToken) {
        if (loading) {
            return <LoadingIndicator variant="ripple" />;
        }
        if (error) {
            return (
                <InfoBox severity="error">
                    Authentication failed. The provided access token was
                    rejected.
                </InfoBox>
            );
        }
        if (!data?.me?.active) {
            return (
                <InfoBox severity="error">
                    Authentication was rejected. The active user is unavailable.
                </InfoBox>
            );
        }
    }

    if (!isAuthenticated) return <WelcomePage />;
    return (
        <GlobalDataProvider>
            <GlobalUiProvider>
                <InfoCardProvider>
                    <GuardianProvider>
                        <PopupProvider>
                            <DialogProvider>
                                <App />
                            </DialogProvider>
                        </PopupProvider>
                    </GuardianProvider>
                </InfoCardProvider>
            </GlobalUiProvider>
        </GlobalDataProvider>
    );
}
